import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetAiWorkerUsageByIdMutation, useAppDispatch } from '@circuitry-ai/doc-data';
import { Button, Spinner, Chip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyShowToast, VoiceFormAssistant, UXGenieTemplateRenderer, useUxgenieTemplateHtmlResolver } from '@circuitry-ai/doc-ui';
import { WorkflowHtmlPreview, UXGenieShareButton } from './uxgenie/components/shared';
import { isHtmlContent } from './uxgenie/utils';

interface UXGenieTemplateViewerProps {
    /** When inside Settings, use this path for back (e.g. /settings/uxgenie) */
    basePath?: string;
}

/**
 * UXGenieTemplateViewer - Full-page viewer for saved UXGenie templates
 * 
 * This component fetches a saved template by ID and renders it with its original data.
 * Uses the unified UXGenieTemplateRenderer component for consistent rendering across the app.
 */
export const UXGenieTemplateViewer: React.FC<UXGenieTemplateViewerProps> = (props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const basePath = props.basePath ?? '/uxgenie';
    const { aiworker_usage_id } = useParams<{ aiworker_usage_id: string }>();
    const dispatch = useAppDispatch();

    const [getTemplateById, { isLoading }] = useGetAiWorkerUsageByIdMutation();
    const { resolveHtml, resolveHtmlUrl } = useUxgenieTemplateHtmlResolver();
    const [templateData, setTemplateData] = useState<any>(null);
    const [renderError, setRenderError] = useState<string | null>(null);
    const [isVoiceCaptureEnabled, setIsVoiceCaptureEnabled] = useState(false);
    const [htmlPreviewSrc, setHtmlPreviewSrc] = useState<string | null>(null);
    const [htmlPreviewContent, setHtmlPreviewContent] = useState<string | null>(null);
    const [isResolvingHtml, setIsResolvingHtml] = useState(false);

    useEffect(() => {
        if (!aiworker_usage_id) return;

        // Holds an in-flight RTK Query mutation handle so it can be aborted below —
        // without this, an effect re-run (e.g. React StrictMode's dev double-invoke,
        // or a remount while the request is pending) leaves the stale request running
        // to completion, producing duplicate network calls for the same id.
        const request = getTemplateById(aiworker_usage_id);

        request
            .unwrap()
            .then((response) => {
                let data = response;
                if (Array.isArray(response) && response.length > 0) {
                    data = response[0];
                } else if (response?.data) {
                    data = Array.isArray(response.data) ? response.data[0] : response.data;
                }

                setTemplateData(data);
            })
            .catch((error) => {
                if (error?.name === 'AbortError') return;
                CtyShowToast('error', t('Failed to load template'));
                setRenderError('Failed to load template data');
            });

        return () => {
            request.abort();
        };
        // Only re-fetch when the usage id changes — avoid duplicate calls from unstable deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiworker_usage_id]);

    // HTML templates are saved to S3 (WAF blocks large inline HTML in the save
    // request) — `generated_code` is empty and the actual content has to be
    // resolved via a presigned URL and loaded directly in the iframe.
    useEffect(() => {
        const outputData = templateData?.action_output || templateData?.output_data;
        if (!outputData || outputData.output_type !== 'html' || outputData.storage !== 's3') return;

        const usageAiworkerId = templateData?.aiworker_id;
        const usageAiworkerUsageId = templateData?.aiworker_usage_id || aiworker_usage_id;
        const sourceId = outputData.source_id;
        const htmlFileName = outputData.html_file_name;
        if (!usageAiworkerId || !usageAiworkerUsageId) return;

        let cancelled = false;
        setIsResolvingHtml(true);
        setHtmlPreviewSrc(null);
        setHtmlPreviewContent(null);

        const resolve = sourceId
            ? resolveHtmlUrl({
                aiworker_id: usageAiworkerId,
                aiworker_usage_id: usageAiworkerUsageId,
                source_id: sourceId,
                extension: htmlFileName?.split('.').pop() || 'html',
            }).then((url) => {
                if (cancelled) return;
                setHtmlPreviewSrc(url);
            })
            // Fallback for templates saved before `source_id` was stored on `action_output`
            : resolveHtml({
                aiworker_id: usageAiworkerId,
                aiworker_usage_id: usageAiworkerUsageId,
                fileName: htmlFileName,
            }).then((content) => {
                if (cancelled) return;
                setHtmlPreviewContent(content);
            });

        resolve
            .catch((error) => {
                if (cancelled) return;
                console.error('Failed to resolve saved HTML template:', error);
                setRenderError('Failed to load template content');
            })
            .finally(() => {
                if (!cancelled) setIsResolvingHtml(false);
            });

        return () => {
            cancelled = true;
        };
    }, [templateData, aiworker_usage_id, resolveHtml, resolveHtmlUrl]);

    const handleBack = () => {
        navigate(basePath);
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
                <Spinner size="lg" className="mb-4" />
                <p className="text-gray-600 text-lg">{t('Loading template...')}</p>
                <p className="text-gray-500 text-sm mt-2">{t('Preparing template with data')}</p>
            </div>
        );
    }

    if (renderError) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
                    <Icon icon="mdi:alert-circle" className="text-red-500 mx-auto mb-4" width={64} />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        {t('Failed to Render Template')}
                    </h2>
                    <p className="text-gray-600 mb-4">{renderError}</p>
                    <Button
                        color="primary"
                        variant="solid"
                        onPress={handleBack}
                        startContent={<Icon icon="mdi:arrow-left" />}
                    >
                        {t('Back to Templates')}
                    </Button>
                </div>
            </div>
        );
    }

    if (!templateData) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
                    <Icon icon="mdi:file-document-remove" className="text-gray-400 mx-auto mb-4" width={64} />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        {t('Template Not Found')}
                    </h2>
                    <p className="text-gray-600 mb-4">
                        {t('The requested template could not be found.')}
                    </p>
                    <Button
                        color="primary"
                        variant="solid"
                        onPress={handleBack}
                        startContent={<Icon icon="mdi:arrow-left" />}
                    >
                        {t('Back to Templates')}
                    </Button>
                </div>
            </div>
        );
    }

    // Extract template code and data
    const generatedCode = templateData?.action_output?.generated_code || templateData?.output_data?.generated_code || '';
    const outputType = templateData?.action_output?.output_type
        || templateData?.output_data?.output_type
        || (isHtmlContent(generatedCode) ? 'html' : 'jsx');
    const isHtmlTemplate = outputType === 'html' || isHtmlContent(generatedCode);
    const originalData = templateData?.input_data?.original_data ||
        templateData?.action_output?.generated_schema ||
        templateData?.output_data?.generated_schema || '';

    const templateName = templateData?.action_output?.action_name ||
        templateData?.title ||
        'Template';
    const savedAt = templateData?.action_output?.saved_at ||
        templateData?.used_at ||
        'N/A';

    return (
        <div className="flex flex-col h-screen bg-gray-50">
            {/* Header */}
            <div className="flex-none bg-white border-b border-gray-200 shadow-sm">
                <div className="w-full max-w-full px-6 py-3">
                    {/* Breadcrumb */}
                    <nav className="flex items-center gap-1.5 text-sm mb-2">
                        <button
                            onClick={handleBack}
                            className="text-gray-500 hover:text-blue-600 transition-colors"
                        >
                            UXGenie Templates
                        </button>
                        <Icon icon="mdi:chevron-right" width={16} className="text-gray-400 flex-shrink-0" />
                        <span className="text-gray-900 font-medium">{templateName}</span>
                    </nav>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                                <Icon icon="mdi:file-code" className="text-white" width={20} />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    {templateName}
                                    <Chip size="sm" variant="flat" color="success">
                                        {t('With Data')}
                                    </Chip>
                                </h1>
                                <p className="text-sm text-gray-500">
                                    {t('Created')}: {new Date(savedAt).toLocaleString()}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            {aiworker_usage_id && (
                                <UXGenieShareButton
                                    templateName={templateName}
                                    usageId={aiworker_usage_id}
                                />
                            )}

                            <VoiceFormAssistant
                                enabled={isVoiceCaptureEnabled}
                                dispatch={dispatch}
                                onToggle={setIsVoiceCaptureEnabled}
                                statusText={{
                                    idle: 'Voice capture idle',
                                    listening: 'Voice capture active',
                                    processing: 'Listening...'
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Template Content */}
            <div className="flex-1 min-h-0 overflow-y-auto bg-gray-50">
                <div className="p-6">
                    <div className="mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        {isHtmlTemplate ? (
                            isResolvingHtml ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <Spinner size="lg" className="mb-4" />
                                    <p className="text-gray-600">{t('Loading template content...')}</p>
                                </div>
                            ) : (
                                <WorkflowHtmlPreview
                                    html={htmlPreviewContent ?? generatedCode}
                                    src={htmlPreviewSrc ?? undefined}
                                />
                            )
                        ) : (
                            <UXGenieTemplateRenderer
                                code={generatedCode}
                                data={originalData}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Footer Info */}
            <div className="flex-none bg-white border-t border-gray-200 px-6 py-3">
                <div className="w-full max-w-full">
                    <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                            <Icon icon="mdi:information-outline" width={16} />
                            <span>
                                {t('Viewing template with original data values.')}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Icon icon="mdi:account" width={16} />
                            <span>{templateData?.user_name || templateData?.user_id || 'N/A'}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UXGenieTemplateViewer;
